import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import { useAudio } from '@/hooks/useAudio';
import { CLASS_DATA } from '@/types';
import type { CharacterClass } from '@/types';

interface SettingsPageProps {
  onClose: () => void;
}

interface GameSettings {
  vibrationEnabled: boolean;
}

const SETTINGS_KEY = 'dw_game_settings';

function loadSettings(): GameSettings {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load settings:', e);
  }
  return { vibrationEnabled: true };
}

function saveSettings(settings: GameSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save settings:', e);
  }
}

export function SettingsPage({ onClose }: SettingsPageProps) {
  const { user, character } = useAuth();
  const { hapticFeedback } = useTelegram();
  const {
    volume,
    setVolume,
    soundEnabled,
    toggleSoundEnabled,
    isMusicPlaying,
    toggleMusic
  } = useAudio();

  const [settings, setSettings] = useState<GameSettings>(loadSettings);

  const classData = character?.characterClass
    ? CLASS_DATA[character.characterClass as CharacterClass]
    : null;

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const handleToggleVibration = () => {
    const newValue = !settings.vibrationEnabled;
    setSettings(prev => ({ ...prev, vibrationEnabled: newValue }));
    if (newValue) {
      hapticFeedback('medium');
    }
  };

  const openLink = (url: string) => {
    hapticFeedback('light');
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Назад
        </button>
        <h1 className="text-lg font-semibold text-white">Настройки</h1>
        <div className="w-16" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-6">
          {/* Profile */}
          <section>
            <h2 className="text-sm font-medium text-gray-400 mb-3">Профиль</h2>
            <div className="bg-gray-800/50 rounded-xl p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                  {user?.photoUrl ? (
                    <img
                      src={user.photoUrl}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl">👤</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white">
                    {user?.firstName} {user?.lastName}
                  </p>
                  {user?.username && (
                    <p className="text-sm text-gray-400">@{user.username}</p>
                  )}
                </div>
              </div>

              {/* Character info */}
              {character && character.characterClass && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚔️</span>
                    <div>
                      <p className="font-medium text-white">{character.name}</p>
                      <p className="text-sm text-gray-400">
                        {classData?.nameRu || character.characterClass} • Уровень {character.level}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Sound Settings */}
          <section>
            <h2 className="text-sm font-medium text-gray-400 mb-3">Звук</h2>
            <div className="bg-gray-800/50 rounded-xl divide-y divide-gray-700/50">
              {/* Background music toggle */}
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Фоновая музыка</p>
                  <p className="text-sm text-gray-400">
                    Атмосферная музыка во время игры
                  </p>
                </div>
                <button
                  onClick={() => {
                    toggleMusic();
                    hapticFeedback('light');
                  }}
                  className={`w-12 h-7 rounded-full transition-colors relative ${
                    isMusicPlaying ? 'bg-amber-500' : 'bg-gray-600'
                  }`}
                >
                  <motion.div
                    animate={{ x: isMusicPlaying ? 20 : 2 }}
                    className="w-6 h-6 bg-white rounded-full absolute top-0.5"
                  />
                </button>
              </div>

              {/* Sound effects toggle */}
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Звуковые эффекты</p>
                  <p className="text-sm text-gray-400">
                    Звуки кубиков и действий
                  </p>
                </div>
                <button
                  onClick={() => {
                    toggleSoundEnabled();
                    hapticFeedback('light');
                  }}
                  className={`w-12 h-7 rounded-full transition-colors relative ${
                    soundEnabled ? 'bg-amber-500' : 'bg-gray-600'
                  }`}
                >
                  <motion.div
                    animate={{ x: soundEnabled ? 20 : 2 }}
                    className="w-6 h-6 bg-white rounded-full absolute top-0.5"
                  />
                </button>
              </div>

              {/* Volume slider */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-white">Громкость</p>
                  <span className="text-sm text-gray-400">{Math.round(volume * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                />
              </div>

              {/* Vibration toggle */}
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Вибрация при бросках</p>
                  <p className="text-sm text-gray-400">
                    Тактильный отклик при броске кубиков
                  </p>
                </div>
                <button
                  onClick={handleToggleVibration}
                  className={`w-12 h-7 rounded-full transition-colors relative ${
                    settings.vibrationEnabled ? 'bg-amber-500' : 'bg-gray-600'
                  }`}
                >
                  <motion.div
                    animate={{ x: settings.vibrationEnabled ? 20 : 2 }}
                    className="w-6 h-6 bg-white rounded-full absolute top-0.5"
                  />
                </button>
              </div>
            </div>
          </section>

          {/* About */}
          <section>
            <h2 className="text-sm font-medium text-gray-400 mb-3">О приложении</h2>
            <div className="bg-gray-800/50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Версия</span>
                <span className="text-gray-200">1.0.0</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Разработчик</span>
                <span className="text-gray-200">@spb780</span>
              </div>
              <div className="pt-2 border-t border-gray-700">
                <p className="text-sm text-amber-500/80 text-center">
                  Powered by Apocalypse World Engine
                </p>
              </div>
            </div>
          </section>

          {/* Links */}
          <section>
            <h2 className="text-sm font-medium text-gray-400 mb-3">Ссылки</h2>
            <div className="space-y-2">
              <button
                onClick={() => openLink('https://t.me/DungeonWorldSupportBot')}
                className="w-full bg-gray-800/50 rounded-xl p-4 text-gray-200 hover:bg-gray-800/70 transition-colors text-left flex items-center gap-3"
              >
                <span className="text-xl">💬</span>
                <span>Поддержка</span>
              </button>
              <button
                onClick={() => openLink('/rules')}
                className="w-full bg-gray-800/50 rounded-xl p-4 text-gray-200 hover:bg-gray-800/70 transition-colors text-left flex items-center gap-3"
              >
                <span className="text-xl">📜</span>
                <span>Правила Dungeon World</span>
              </button>
              <button
                onClick={() => openLink('#')}
                className="w-full bg-gray-800/50 rounded-xl p-4 text-gray-200 hover:bg-gray-800/70 transition-colors text-left flex items-center gap-3"
              >
                <span className="text-xl">🔒</span>
                <span>Политика конфиденциальности</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
